import { useMemo } from 'react';

import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject, PropertyValue } from '@/types/crm';

import { useMergeWizard } from './MergeWizardContext';
import { useMergePropertyResolution } from './useMergePropertyResolution';

const EMPTY_OBJECT: CrmObject = {
  id: '',
  org_id: 0,
  entity_id: '',
  entity_type_name: 'ahoy_contact',
  properties: {},
  last_event_seq: 0,
  deleted_at: null,
};

/**
 * The canonical record with every Step 2 resolution applied — the final merged
 * property state. Shared by Step 2's live preview and Step 3's read-only review
 * so the two never diverge. Returns `undefined` while the canonical is loading.
 */
export function useMergedPreviewObject(
  canonicalObject: CrmObject | undefined,
  sourceObject: CrmObject | undefined,
  schema: PropertySchemaMap
): CrmObject | undefined {
  const { singlePropertyResolutions, multiPropertyResolutions } =
    useMergeWizard();
  const resolution = useMergePropertyResolution(
    canonicalObject ?? EMPTY_OBJECT,
    sourceObject ?? EMPTY_OBJECT,
    schema
  );

  return useMemo(() => {
    if (!canonicalObject) {
      return undefined;
    }
    const properties = { ...(canonicalObject.properties ?? {}) };

    for (const r of resolution.conflicts) {
      const chosen =
        singlePropertyResolutions[r.propertyName]?.winner ?? r.winner;
      if (chosen === 'source' && r.sourceValue !== null) {
        properties[r.propertyName] = r.sourceValue;
      }
    }

    for (const r of resolution.autoResolved) {
      if (r.winner === 'source' && r.sourceValue !== null) {
        properties[r.propertyName] = r.sourceValue;
      }
    }

    for (const m of resolution.multiValue) {
      const userOverride = multiPropertyResolutions[m.propertyName];
      const items = userOverride ?? m;
      const kept = [...items.canonicalItems, ...items.sourceItems]
        .filter(it => it.keep)
        .map(it => it.value.value);
      properties[m.propertyName] = { value: kept } as PropertyValue;
    }

    return { ...canonicalObject, properties };
  }, [
    canonicalObject,
    resolution,
    singlePropertyResolutions,
    multiPropertyResolutions,
  ]);
}
